import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { movies, type Movie } from '@/mocks/movies';
import { genreOptions, yearOptions, countryOptions } from '@/mocks/categories';
import { useFavorites } from '@/hooks/useFavorites';
import { useToast } from '@/hooks/useToast';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import Toast from '@/components/base/Toast';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Pagination from '@/components/feature/Pagination';
import MovieCard from '@/components/feature/MovieCard';
import FilterSidebar from './components/FilterSidebar';

const PAGE_SIZE = 8;

const parseList = (value: string | null): string[] =>
  value ? value.split(',').map((v) => v.trim()).filter(Boolean) : [];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const { isFavorite, toggleFavorite } = useFavorites();
  const { toast, showToast } = useToast();
  const [filterOpen, setFilterOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const query = searchParams.get('q') ?? '';
  const selectedGenres = parseList(searchParams.get('genre'));
  const selectedYears = parseList(searchParams.get('year'));
  const selectedCountries = parseList(searchParams.get('country'));
  const sort = searchParams.get('sort') ?? 'year';
  const view = searchParams.get('view') === 'list' ? 'list' : 'grid';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);

  usePageMeta({
    title: '北条麻妃电影分类 - 影视作品大全',
    description:
      '北条麻妃电影分类页提供完整的作品大全，支持按剧情、爱情、悬疑等多类型以及年代、国家多维筛选，快速定位你想了解的电影。',
    keywords: '北条麻妃电影分类,影视作品大全,电影筛选,电影列表,剧情片,爱情片',
  });

  const updateParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    });
    if (!('page' in updates)) next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const toggleFilter = (group: 'genre' | 'year' | 'country', value: string) => {
    const current =
      group === 'genre' ? selectedGenres : group === 'year' ? selectedYears : selectedCountries;
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateParams({ [group]: next.length ? next.join(',') : null });
  };

  const resetFilters = () => updateParams({ genre: null, year: null, country: null, q: null });

  const filtered = useMemo(() => {
    let list = movies.filter((m) => {
      if (query && !m.title.toLowerCase().includes(query.toLowerCase())) return false;
      if (selectedGenres.length && !m.genres.some((g) => selectedGenres.includes(g))) return false;
      if (selectedYears.length && !selectedYears.includes(String(m.year))) return false;
      if (selectedCountries.length && !selectedCountries.includes(m.country)) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sort === 'title') return a.title.localeCompare(b.title, 'zh-Hans-CN');
      return b.year - a.year;
    });
    return list;
  }, [query, selectedGenres, selectedYears, selectedCountries, sort]);

  const filterKey = `${query}|${selectedGenres.join(',')}|${selectedYears.join(',')}|${selectedCountries.join(',')}|${sort}`;

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filterKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const desktopItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const mobileItems = filtered.slice(0, visibleCount);
  const items = isDesktop ? desktopItems : mobileItems;

  const handleToggleFavorite = (movie: Movie) => {
    const added = toggleFavorite(movie.id);
    showToast(
      added ? `已收藏《${movie.title}》` : `已取消收藏《${movie.title}》`,
      added ? 'accent' : 'default',
    );
  };

  const activeChips = [
    ...selectedGenres.map((v) => ({ group: 'genre' as const, value: v })),
    ...selectedYears.map((v) => ({ group: 'year' as const, value: v })),
    ...selectedCountries.map((v) => ({ group: 'country' as const, value: v })),
  ];

  return (
    <>
      <section className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '电影分类' }]} />
          <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">
            {query ? `“${query}” 的搜索结果` : '北条麻妃电影分类'}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            这里汇集了北条麻妃的全部影视作品，你可以按类型、年代、国家进行组合筛选，并按上映时间或标题排序，快速找到想看的那一部。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
          <div className={`${isDesktop || filterOpen ? 'block' : 'hidden'} lg:block`}>
            <FilterSidebar
              genres={genreOptions}
              years={yearOptions}
              countries={countryOptions}
              selectedGenres={selectedGenres}
              selectedYears={selectedYears}
              selectedCountries={selectedCountries}
              resultCount={filtered.length}
              onToggle={toggleFilter}
              onReset={resetFilters}
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
              <button
                type="button"
                onClick={() => setFilterOpen((v) => !v)}
                className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line text-base"></i>
                筛选
                {activeChips.length > 0 && (
                  <span className="rounded-full bg-accent-500 px-1.5 text-xs text-background-50">
                    {activeChips.length}
                  </span>
                )}
              </button>

              <p className="hidden text-sm text-foreground-500 lg:block">
                共 <span className="font-semibold text-foreground-800">{filtered.length}</span> 部作品
              </p>

              <div className="flex items-center gap-2">
                <div className="relative">
                  <select
                    value={sort}
                    onChange={(e) => updateParams({ sort: e.target.value })}
                    aria-label="排序方式"
                    className="appearance-none rounded-md border border-background-300 bg-background-50 py-2 pl-3 pr-8 text-sm text-foreground-700 outline-none focus:border-primary-400"
                  >
                    <option value="year">按上映时间</option>
                    <option value="title">按标题</option>
                  </select>
                  <i className="ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                </div>

                <div className="hidden items-center gap-1 rounded-md border border-background-300 p-1 sm:flex">
                  <button
                    type="button"
                    onClick={() => updateParams({ view: null })}
                    aria-label="网格视图"
                    className={`flex h-7 w-7 items-center justify-center rounded ${
                      view === 'grid' ? 'bg-primary-500 text-background-50' : 'text-foreground-500'
                    }`}
                  >
                    <i className="ri-grid-fill text-sm"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => updateParams({ view: 'list' })}
                    aria-label="列表视图"
                    className={`flex h-7 w-7 items-center justify-center rounded ${
                      view === 'list' ? 'bg-primary-500 text-background-50' : 'text-foreground-500'
                    }`}
                  >
                    <i className="ri-list-check text-sm"></i>
                  </button>
                </div>
              </div>
            </div>

            {activeChips.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {activeChips.map((chip) => (
                  <button
                    key={`${chip.group}-${chip.value}`}
                    type="button"
                    onClick={() => toggleFilter(chip.group, chip.value)}
                    className="flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
                  >
                    {chip.value}
                    <i className="ri-close-line"></i>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-xs font-medium text-accent-600 transition-colors hover:text-accent-700"
                >
                  清除全部
                </button>
              </div>
            )}

            {items.length === 0 ? (
              <div className="mt-16 flex flex-col items-center text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-movie-line text-2xl"></i>
                </span>
                <h2 className="mt-5 text-lg font-semibold text-foreground-900">
                  抱歉，没有找到符合条件的电影。
                </h2>
                <p className="mt-2 max-w-md text-sm text-foreground-500">
                  试试更换关键词或减少筛选条件，也许会有新的发现。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
                >
                  重置筛选
                </button>
              </div>
            ) : view === 'grid' ? (
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
                {items.map((movie, i) => (
                  <MovieCard
                    key={movie.id}
                    movie={movie}
                    index={i}
                    isFavorite={isFavorite(movie.id)}
                    onToggleFavorite={handleToggleFavorite}
                  />
                ))}
              </div>
            ) : (
              <div className="mt-6 flex flex-col gap-3 md:gap-4">
                {items.map((movie, i) => (
                  <MovieCard
                    key={movie.id}
                    movie={movie}
                    variant="list"
                    index={i}
                    isFavorite={isFavorite(movie.id)}
                    onToggleFavorite={handleToggleFavorite}
                  />
                ))}
              </div>
            )}

            {items.length > 0 && (
              <>
                {isDesktop ? (
                  <Pagination
                    current={page}
                    totalPages={totalPages}
                    onChange={(p) => updateParams({ page: String(p) })}
                  />
                ) : (
                  filtered.length > visibleCount && (
                    <div className="mt-8 flex justify-center">
                      <button
                        type="button"
                        onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                        className="rounded-full border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 whitespace-nowrap"
                      >
                        加载更多作品
                      </button>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Toast toast={toast} />
    </>
  );
}